import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { BehaviorSubject, combineLatest, filter, map, Observable, startWith, switchMap } from 'rxjs';
import { VariableBenefitConfigurationJurisdictionPaymentFormGroup } from '../../form-groups/variable-benefit-configuration-jurisdiction-payment-form-group';
import { RecurringPaymentAmountTypeEnum } from '../../enums/recurring-payment-amount-type.enum';
import { VariableBenefitPaymentFrequencyEnum } from '../../../../../../models/enum/shared/variable-benefit-payment-frequency.enum';
import { FormErrorMessages } from '../../../../../../ui/form-components/form-error/form-error-messages';
import { StatusPill } from '../../../../../../models/shared/status-pill';
import { FormControl } from '@angular/forms';
import { VbTargetSplitYearCapacity } from '../../utils/vb-target-split-warning-utils';

@Component({
  selector: 'app-vb-jurisdiction-recurring-payment-card',
  templateUrl: './vb-jurisdiction-recurring-payment-card.component.html',
  styleUrls: ['./vb-jurisdiction-recurring-payment-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default
})
export class VbJurisdictionRecurringPaymentCardComponent {
  @Input({ required: true }) set form(value: VariableBenefitConfigurationJurisdictionPaymentFormGroup) {
    this._form.next(value);
  }
  @Input({ required: true }) set paymentFrequency(value: VariableBenefitPaymentFrequencyEnum) {
    this._paymentFrequency.next(value);
  }
  @Input() estimatedBalance: number = 0;
  @Input() estimatedAnnualTotal: number = 0;
  @Input() isLoadingAnnualTotal: boolean = false;
  @Input() minAnnualAmount: number = 0;
  @Input() maxAnnualAmount: number | null = null;
  @Input() minimumPaymentAmount = 0;
  @Input() maximumPaymentAmount = 0;
  @Input() maximumRaised = false;
  @Input() formSubmitted: boolean = false;
  @Input() isBoardDirected: boolean | null = false;
  @Input() isNew: boolean = false;
  @Input() isTargetedPayment: boolean = false;
  @Input() calculatingTargetedPayment = false;
  @Input() balanceLabel = $localize`Est. Balance:`;
  @Input() startsInALaterYear = false;
  @Input() yearCapacity: VbTargetSplitYearCapacity | null = null;
  @Input() set refusal(value: string | null) {
    this._refusal.next(value);
  }

  private _paymentFrequency = new BehaviorSubject<VariableBenefitPaymentFrequencyEnum | null>(null);
  public paymentFrequency$ = this._paymentFrequency.asObservable();

  private _refusal = new BehaviorSubject<string | null>(null);

  private _form = new BehaviorSubject<VariableBenefitConfigurationJurisdictionPaymentFormGroup | null>(null);
  public form$ = this._form.asObservable();

  private governingJurisdictionId$ = this._form.pipe(
    filter(Boolean),
    switchMap(form =>
      form.controls.governingJurisdictionId.valueChanges.pipe(startWith(form.controls.governingJurisdictionId.value))
    )
  );

  public loadingControl = new FormControl({ value: '', disabled: true });

  public minTooltip$ = this._paymentFrequency.pipe(
    map(freq => {
      return freq === VariableBenefitPaymentFrequencyEnum.Monthly
        ? $localize`A payment amount that pays what's left of the Annual Min for the year, spread over the payments left from the start date. If the Annual Min is met before year's end, payments will pause until January of next year. If necessary, the December payment will be topped up to a value that ensures the Annual Min is met.`
        : $localize`A payment that matches the account's Annual Min when no other payments have occurred for the year. If payments have been made, opting into this Minimum Payment will ensure that the annual payment amount is adjusted accordingly.`;
    })
  );

  public maxTooltip$ = combineLatest([this._paymentFrequency, this.governingJurisdictionId$]).pipe(
    map(([freq, jurisdictionId]) => this.buildMaxTooltip(freq, jurisdictionId))
  );

  public customErrorMessages$: Observable<Record<string, FormErrorMessages>> = combineLatest([
    this.governingJurisdictionId$,
    this._refusal
  ]).pipe(map(([jurisdictionId, refusal]) => this.buildCustomErrorMessages(jurisdictionId, refusal)));

  public newStatusPill = new StatusPill(0, $localize`New`, '#0D5D4B', '#E8F3F1');

  private buildMaxTooltip(freq: VariableBenefitPaymentFrequencyEnum | null, jurisdictionId: number | null): string {
    if (freq !== VariableBenefitPaymentFrequencyEnum.Monthly) {
      return $localize`A payment that matches the account's Annual Max when no other payments have occurred for the year. If payments have been made, opting into this Maximum Payment will ensure that the annual payment amount is adjusted accordingly, so that the Annual Max for the account is not exceeded.`;
    }
    return jurisdictionId === null
      ? $localize`A payment amount that pays out the account's balance, spread over the payments left in the year from the start date.`
      : $localize`A payment amount that pays what's left of the Annual Max for the year, spread over the payments left from the start date. If the Annual Max is met before year's end, payments will pause until January of next year. If necessary, the December payment will be topped up to a value that ensures the Annual Max is met and not exceeded.`;
  }

  private buildCustomErrorMessages(jurisdictionId: number | null, refusal: string | null) {
    const enterAmountMessage =
      jurisdictionId === null
        ? $localize`Enter a custom amount, or select the Minimum Payment`
        : $localize`Enter a custom amount, or select the Minimum Payment or Maximum Payment`;

    return {
      estimatedMax: { exceedsMaximum: refusal ?? '' },
      recurringPaymentAmount: {
        max: $localize`The amount can not exceed the account's Est. Balance`,
        required: enterAmountMessage,
        min: enterAmountMessage
      }
    };
  }

  protected readonly RecurringPaymentAmountTypeEnum = RecurringPaymentAmountTypeEnum;
  protected readonly VariableBenefitPaymentFrequencyEnum = VariableBenefitPaymentFrequencyEnum;
}
